/**
 * Support Tickets API Service
 *
 * Wraps the in-portal Contact Us / support-ticket endpoints exposed by the
 * API. Provides typed helpers for both sides of the conversation:
 * - User-facing endpoints under `/support-tickets`
 * - Admin-facing endpoints under `/admin/support-tickets`
 *
 * The DTO shapes mirror the backend (see ScheduledSmiles.Api/DTOs/Support*).
 * Enum values are kept in sync with the backend numeric enums; the API
 * serializes them as strings via `JsonStringEnumConverter`, which means the
 * wire format is the enum NAME — so the union types below use string
 * literals matching the C# member names verbatim.
 */

import apiClient from './apiClient';

/** Categories available when filing a new ticket. Mirrors `SupportTicketCategory`. */
export const SUPPORT_TICKET_CATEGORIES = [
  'GeneralQuestion',
  'Support',
  'BugReport',
  'FeatureRequest',
  'Billing',
  'Account',
] as const;

export type SupportTicketCategory = (typeof SUPPORT_TICKET_CATEGORIES)[number];

/** Workflow status. Mirrors `SupportTicketStatus`. */
export const SUPPORT_TICKET_STATUSES = [
  'Open',
  'AwaitingAdmin',
  'AwaitingUser',
  'Resolved',
  'Closed',
] as const;

export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

/** Authorship of a message. Mirrors `SupportTicketSenderType`. */
export type SupportTicketSenderType = 'User' | 'Admin' | 'System';

/**
 * Human-readable labels for the category dropdown. Co-located with the enum
 * so any new category is one source of truth (TypeScript will surface a
 * missing key immediately).
 */
export const SUPPORT_TICKET_CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  GeneralQuestion: 'General Question',
  Support: 'Support / Help',
  BugReport: 'Bug Report',
  FeatureRequest: 'Feature Request',
  Billing: 'Billing / Subscription',
  Account: 'Account / Login',
};

/** Human-readable labels for the status badge. */
export const SUPPORT_TICKET_STATUS_LABELS: Record<SupportTicketStatus, string> = {
  Open: 'Open',
  AwaitingAdmin: 'Awaiting Support',
  AwaitingUser: 'Awaiting Your Reply',
  Resolved: 'Resolved',
  Closed: 'Closed',
};

/** One row in a paginated response from the API. */
export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Compact ticket header used in list views. */
export interface SupportTicketSummary {
  id: number;
  subject: string;
  category: SupportTicketCategory;
  status: SupportTicketStatus;
  lastMessageAt: string;
  createdAt: string;
  unreadForCurrentSide: boolean;
}

/** Admin list rows include owner contact info + has-internal-notes flag. */
export interface AdminSupportTicketSummary {
  summary: SupportTicketSummary;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  hasInternalNotes: boolean;
}

/** One message inside a ticket thread. */
export interface SupportTicketMessage {
  id: number;
  senderType: SupportTicketSenderType;
  senderDisplayName: string;
  body: string;
  createdAt: string;
  isInternal: boolean;
}

/** Full ticket detail returned by GET endpoints. */
export interface SupportTicketDetail {
  summary: SupportTicketSummary;
  messages: SupportTicketMessage[];
  /** False when the ticket is in a terminal state and no replies are accepted. */
  canReply: boolean;
}

/** Request body for `POST /support-tickets`. */
export interface CreateSupportTicketRequest {
  subject: string;
  category: SupportTicketCategory;
  initialMessage: string;
}

/** Request body for `POST /support-tickets/{id}/messages`. */
export interface CreateSupportTicketMessageRequest {
  body: string;
  /**
   * Admin-only. Internal notes are never visible to the user and never
   * trigger an email notification. Ignored on the user-facing endpoint.
   */
  isInternal?: boolean;
}

/** Filter criteria accepted by the admin list endpoint. */
export interface AdminSupportTicketsQuery {
  page?: number;
  pageSize?: number;
  status?: SupportTicketStatus;
  category?: SupportTicketCategory;
  search?: string;
}

const userBase = '/support-tickets';
const adminBase = '/admin/support-tickets';

/**
 * Creates a new support ticket on behalf of the calling user.
 *
 * Rate-limited via the API's `SupportTicketsCreate` policy — the apiClient
 * 429 interceptor surfaces a friendly message to the caller.
 */
export const createSupportTicket = async (
  request: CreateSupportTicketRequest
): Promise<SupportTicketDetail> => {
  const response = await apiClient.post<SupportTicketDetail>(userBase, request);
  return response.data;
};

/** Fetches the current user's tickets, paged by most recent activity. */
export const getMySupportTickets = async (
  page = 1,
  pageSize = 20
): Promise<PagedResult<SupportTicketSummary>> => {
  const response = await apiClient.get<PagedResult<SupportTicketSummary>>(userBase, {
    params: { page, pageSize },
  });
  return response.data;
};

/**
 * Fetches a single ticket the user owns. The server stamps "last viewed by
 * user" as a side-effect so the unread badge clears.
 */
export const getMySupportTicket = async (id: number): Promise<SupportTicketDetail> => {
  const response = await apiClient.get<SupportTicketDetail>(`${userBase}/${id}`);
  return response.data;
};

/** Appends a user reply to one of the calling user's tickets. */
export const replyToSupportTicket = async (
  id: number,
  request: CreateSupportTicketMessageRequest
): Promise<SupportTicketDetail> => {
  const response = await apiClient.post<SupportTicketDetail>(
    `${userBase}/${id}/messages`,
    { body: request.body }
  );
  return response.data;
};

/** Returns the current user's unread-tickets count for the inbox badge. */
export const getMySupportUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<{ count: number }>(`${userBase}/unread-count`);
  return response.data.count;
};

/** Admin: paginated list of all tickets, filterable by status/category/search. */
export const getAdminSupportTickets = async (
  query: AdminSupportTicketsQuery = {}
): Promise<PagedResult<AdminSupportTicketSummary>> => {
  const response = await apiClient.get<PagedResult<AdminSupportTicketSummary>>(adminBase, {
    params: {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 25,
      status: query.status,
      category: query.category,
      search: query.search,
    },
  });
  return response.data;
};

/**
 * Admin: full ticket detail including internal notes. Stamps "last viewed
 * by admin" on the server so the admin nav badge clears.
 */
export const getAdminSupportTicket = async (id: number): Promise<SupportTicketDetail> => {
  const response = await apiClient.get<SupportTicketDetail>(`${adminBase}/${id}`);
  return response.data;
};

/**
 * Admin: append a reply to a ticket. Setting `isInternal=true` posts an
 * admin-only note that does NOT trigger an email or transition the status.
 */
export const replyAsAdmin = async (
  id: number,
  request: CreateSupportTicketMessageRequest
): Promise<SupportTicketDetail> => {
  const response = await apiClient.post<SupportTicketDetail>(
    `${adminBase}/${id}/messages`,
    request
  );
  return response.data;
};

/** Admin: manually transition a ticket to a new status (e.g. Resolved/Closed). */
export const updateSupportTicketStatus = async (
  id: number,
  status: SupportTicketStatus
): Promise<SupportTicketDetail> => {
  const response = await apiClient.put<SupportTicketDetail>(`${adminBase}/${id}/status`, {
    status,
  });
  return response.data;
};

/** Admin: count of tickets where the user has spoken last since the admin viewed it. */
export const getAdminSupportUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<{ count: number }>(`${adminBase}/unread-count`);
  return response.data.count;
};

/** Admin: permanently delete one ticket and its thread (PBI #1538). */
export const deleteAdminSupportTicket = async (id: number): Promise<void> => {
  await apiClient.delete(`${adminBase}/${id}`);
};

/** Admin: permanently delete many tickets in one request (PBI #1538). */
export const batchDeleteAdminSupportTickets = async (
  ticketIds: number[],
): Promise<{ deletedCount: number }> => {
  const response = await apiClient.post<{ deletedCount: number }>(
    `${adminBase}/batch-delete`,
    { ticketIds },
  );
  return response.data;
};
